"use client";

import { useEffect, useState } from "react";
import { safeParseLessonContent } from "@/lib/lesson-content/schema";
import { clearIleSessionProgress } from "@/lib/ile/session-storage";
import { LessonProgressProvider } from "@/components/ile/LessonProgressProvider";
import InteractiveLessonRunner from "@/components/ile/InteractiveLessonRunner";

/**
 * Admin-side preview of a draft lesson, rendered with Dev 2's student runner so
 * the preview matches exactly what students will see. Validates the in-memory
 * draft first; on failure shows the Zod issues instead of the lesson.
 *
 * Mirrors the student wiring (`LessonProgressProvider` → `InteractiveLessonRunner`)
 * with a fixed lessonId ("admin-preview") so its sessionStorage progress is
 * isolated from real lessons. Interactions + section navigation work for testing.
 */
export default function IleAdminPreview({ draft, lessonTitle }: { draft: unknown; lessonTitle?: string }) {
  // A fresh session id per mount so each Preview starts clean — otherwise the
  // runner's sessionStorage (keyed by lessonId) carries a stale "completed"
  // state across navigations / Edit↔Preview toggles and looks stuck. Clear it on
  // unmount so these throwaway preview sessions don't accumulate.
  const [previewId] = useState(() => `admin-preview-${crypto.randomUUID()}`);
  useEffect(() => () => clearIleSessionProgress(previewId), [previewId]);

  const parsed = safeParseLessonContent(draft);

  if (!parsed.success) {
    return (
      <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
        <p className="font-semibold">Preview unavailable — invalid lesson JSON</p>
        <ul className="mt-2 list-disc pl-5">
          {parsed.error.issues.map((issue, i) => (
            <li key={i}>
              {issue.path.join(".") || "(root)"}: {issue.message}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  // Wrap in the SAME container the student LessonPlayer uses for the lesson body
  // (montserrat font + centered max-w-5xl white column + matching padding) so the
  // preview is pixel-faithful to what learners see, not a bare admin-styled render.
  return (
    <div className="font-montserrat">
      <div className="mx-auto w-full max-w-5xl rounded-xl border border-slate-200 bg-white px-4 pb-6 pt-6 sm:px-6 lg:px-10">
        <LessonProgressProvider content={parsed.data} lessonId={previewId} persistPoints={false}>
          <InteractiveLessonRunner showSectionNav lessonTitle={lessonTitle} />
        </LessonProgressProvider>
      </div>
    </div>
  );
}
